import React, { useContext, useEffect } from "react";
import { FormikProps } from "formik";
import LaboratoriesApi from "../../../api/services/LaboratoriesApi";
import { AutocompleteSelectable } from "../../../components/Autocomplete/AutocompleteSelectable.model";
import useEnv from "../../../hooks/env";
import { AssignableUsersMultipleAutocomplete } from "../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import { NavigationContext } from "../../../contexts/NavigationContext";
import { ConfirmationDialogContext } from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import confirmationDialogConfig from "../constants/ConfirmationDialogConfig";

export interface AssignableUsersInputProps {
  laboratoryId: number;
  disabled: boolean;
  autoFocus?: boolean;
  mandatory?: boolean;
  reset?: boolean;
  canPreventNavigation: boolean;
  formRef: React.RefObject<
    FormikProps<{
      users: {
        id: number;
        name: string;
      }[];
    }>
  >;
  values: { users: { id: number; name: string }[] };
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  handleSetReseting?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AssignableUsersInput: React.FC<AssignableUsersInputProps> = (props) => {
  const {
    laboratoryId,
    autoFocus,
    disabled,
    mandatory,
    values,
    formRef,
    canPreventNavigation,
    setFieldValue,
    reset,
    handleSetReseting,
  } = props;
  const { api } = useEnv();
  const laboratoriesApi = new LaboratoriesApi(api);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { updateDirtyFlag, updatePreventNav } = useContext(NavigationContext);

  useEffect(() => {
    if (reset && formRef && formRef.current) {
      setFieldValue("users", formRef.current.initialValues.users);
    }
    if (handleSetReseting) handleSetReseting(false);
  }, [reset, values.users]);

  const handleUpdatePreventNav = () => {
    return showConfirmationDialog(confirmationDialogConfig).then((data: string) => {
      if (formRef && formRef.current) {
        if (data === "DEFAULT") {
          formRef.current?.submitForm();
        } else {
          formRef.current.setFieldValue("users", formRef.current.initialValues.users);
          if (canPreventNavigation) {
            updateDirtyFlag(true);
          }
        }
      }
    });
  };

  const deleteTagAtIndex = (item: AutocompleteSelectable<{ id: number; name: string }>, index: number) => {
    return {
      onDelete: () => {
        setFieldValue(
          "users",
          values.users.filter((_, i) => i !== index),
        );
        if (canPreventNavigation) {
          updateDirtyFlag(true);
          updatePreventNav(handleUpdatePreventNav);
        }
      },
    };
  };

  return (
    values.users && (
      <AssignableUsersMultipleAutocomplete
        autoFocus={!!autoFocus}
        disabled={disabled}
        mandatory={mandatory}
        inputLabel="Assignable Users"
        placeholder="Add User"
        map={(value): { key: string; label: string } => {
          return { key: `${value.id}`, label: `${value.name}` };
        }}
        onChange={(event, newUsers): void => {
          setFieldValue("users", newUsers);
          if (canPreventNavigation) {
            updateDirtyFlag(true);
          }
        }}
        optionFetch={(query) => {
          return laboratoriesApi.getAssignableUsers(laboratoryId, query).then(({ data }) => {
            return data;
          });
        }}
        value={values.users}
        getCustomTagDelete={deleteTagAtIndex}
      />
    )
  );
};

export default AssignableUsersInput;
